import React, { useEffect, useRef, useState, useCallback } from "react";

const AudioSink = ({ id, stream }) => {
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
      // Try to play; ignore autoplay errors (mobile browsers may block until interaction)
      const play = async () => {
        try {
          await audioRef.current.play();
        } catch (e) {}
      };
      play();
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.srcObject = null;
      }
    };
  }, [stream]);

  return <audio ref={audioRef} autoPlay playsInline />;
};

const GlobalAudioManager = () => {
  const [streamsById, setStreamsById] = useState({});

  const addStream = useCallback((id, stream) => {
    if (!id || !stream) return;
    setStreamsById((prev) => {
      return { ...prev, [id]: stream };
    });
  }, []);

  const removeStream = useCallback((id) => {
    if (!id) return;
    setStreamsById((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setStreamsById({});
  }, []);

  useEffect(() => {
    // Signal presence so other components can avoid duplicate playback
    try {
      window.__GLOBAL_AUDIO_ENABLED__ = true;
    } catch (e) {}
    const handleAdd = (e) => {
      const { id, stream } = e.detail || {};
      addStream(id, stream);
    };
    const handleRemove = (e) => {
      const { id } = e.detail || {};
      removeStream(id);
    };
    const handleCallEnded = () => {
      clearAll();
    };

    window.addEventListener("global-audio:add", handleAdd);
    window.addEventListener("global-audio:remove", handleRemove);
    window.addEventListener("callEnded", handleCallEnded);
    window.addEventListener("beforeunload", handleCallEnded);
    return () => {
      try {
        window.__GLOBAL_AUDIO_ENABLED__ = false;
      } catch (e) {}
      window.removeEventListener("global-audio:add", handleAdd);
      window.removeEventListener("global-audio:remove", handleRemove);
      window.removeEventListener("callEnded", handleCallEnded);
      window.removeEventListener("beforeunload", handleCallEnded);
    };
  }, [addStream, removeStream, clearAll]);

  return (
    <div style={{ display: "none" }}>
      {Object.entries(streamsById).map(([id, stream]) => (
        <AudioSink key={id} id={id} stream={stream} />
      ))}
    </div>
  );
};

export default GlobalAudioManager;
